import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { APP_VERSION_INFO, DATA_GEN_CONTRIBUTIONS, isXappCapEmbedMode, provideFramework, WHITEBOARD_CONFIG } from '@optima-stride-ai/optima-app-framework';
import { HayagrivaDataGenPanelComponent } from './data-gen/hayagriva-data-gen-panel.component';
import { routes } from './app.routes';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { provideOptimaUI } from '@optima-stride-ai/optima-components';
import { GlobalStateService } from '../service/global.service';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import packageJson from '../../package.json';
import frameworkPackageJson from '@optima-stride-ai/optima-app-framework/package.json';


/**
 * Initialize query parameter storage before app starts
 * This captures query params before auth guards redirect to login
 */
function initializeQueryParams(globalService: GlobalStateService) {
  return () => {
    // Get query parameters from the current URL
    const urlParams = new URLSearchParams(window.location.search);

    // Check if we have the special query parameters
    const isOpen = urlParams.get('isOpen');
    const id = urlParams.get('id');
    const parentId = urlParams.get('parentId');
    const typeId = urlParams.get('typeId');
    const addNew = urlParams.get('isNew');

    if (isOpen || id || addNew) {
      // Store them in global service for post-login restoration
      const params: any = {};
      if (isOpen) params.isOpen = isOpen;
      if (id) params.id = id;
      if (parentId) params.parentId = parentId;
      if (typeId) params.typeId = typeId;
      if (addNew) params.isNew = addNew;

      globalService.storeQueryParameters(params);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeQueryParams,
      deps: [GlobalStateService],
      multi: true
    },
    {
      provide: WHITEBOARD_CONFIG,
      useValue: {
        yjsWsUrl: environment.whiteboard?.yjsWsUrl ?? 'wss://yjs-api.optimastride.com',
        useYjsRealtime: environment.whiteboard?.useYjsRealtime ?? true
      }
    },
 {
      provide: APP_VERSION_INFO,
      useValue: {
        appName:  environment.displayName,
        appVersion: packageJson.version || '0.0.0',
        apiVersion: environment.hayagriva.appServer.version,
        optimaCoreVersion: packageJson.dependencies['@optima-stride-ai/core'],
        optimaFrameworkVersion: frameworkPackageJson.version,
        optimaComponentsVersion: packageJson.dependencies['@optima-stride-ai/optima-components'],
        optimaPlatformVersion: packageJson.dependencies['@optima-stride-ai/optima-platform'],
      }
    },
    provideFramework({
      // defaultAppId: environment.appId,
      appId: environment.appId,
      servingEnvironment: environment.appEnvironment,
      subdomain: environment.subdomain,
      debug: environment.debugMode,
      platform: {
        api: {
          baseURL: environment.hayagriva.appServer.baseURL,
          ext: environment.hayagriva.appServer.extension,
          version: environment.hayagriva.appServer.version
        }
      },
      enablePlatformBootstrap: true,
      // Embedded viewers need auth/rights, but not the app switcher or shell metadata.
      embedded: isXappCapEmbedMode()
    }),
    provideOptimaUI({
      providePrime: true,
      // provideOptimaComponents: true,
    }),
    {
      provide: DATA_GEN_CONTRIBUTIONS,
      useValue: {
        appName: environment.appName,
        displayName: environment.displayName,
        routePath: 'hayagriva',
        icon: 'cilLibrary',
        component: HayagrivaDataGenPanelComponent,
        hideSeedTab: true,
      },
      multi: true,
    },
    // PrimeNG theme configuration with dark mode selector disabled
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false
        }
      }
    }),
  ]
};
